import { useState } from 'react';
import { etiquetaDe, type Plantilla } from '../../../api/ops/listas';
import { ErrorApi } from '../../../api/problema';
import { Boton } from '../../../componentes/Boton';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { usePermisos } from '../../../permisos/permisos';
import { Bloque, Botones, Caja, Dato } from '../comun';
import { useAvisarError } from '../uso';
import { acompanantes, ajustesDeFormulario, colorDe, erroresLista, formularioDeLista, opsDe, PERMISOS, RUTA_CANALES_DE_ENVIO, type FormularioLista } from '../textos';
import type { ContextoListas } from './ApartadoListas';
import { FormLista } from './FormLista';

/**
 * Plantilla de lista (10.9): crear (`createGuestListTemplate`), cambiar (`updateGuestListTemplate` con `If-Match`; no toca
 * las listas ya creadas) y archivar (`archiveGuestListTemplate`; nunca se borra). «Copiarla a cada evento nuevo» es
 * `apply_by_default`.
 */
export function PanelPlantilla({ ctx, plantilla }: { ctx: ContextoListas; plantilla: Plantilla | null }) {
    const { props } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [f, setF] = useState<FormularioLista>(() => formularioDeLista(plantilla));
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const puede = permisos.puede(PERMISOS.plantillas) && !soloLectura && (plantilla === null || plantilla.status === 'active');
    const errores = probado ? erroresLista(f) : {};

    const guardar = () => {
        if (Object.keys(erroresLista(f)).length > 0) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        const datos = { ...ajustesDeFormulario(f, plantilla?.door_price?.currency ?? 'EUR'), door_cutoff_local_time: f.horaLimite === '' ? null : f.horaLimite, apply_by_default: f.aCadaEvento };
        const peticion = plantilla === null ? ops.createGuestListTemplate(negocio, datos) : ops.updateGuestListTemplate(negocio, plantilla.id, datos, etiquetaDe(plantilla));
        peticion
            .then((t) => {
                avisar({ tipo: 'ok', texto: plantilla === null ? `Plantilla «${t.name ?? ''}» creada.${t.apply_by_default ? ' Se copiará a cada evento nuevo.' : ''}` : 'Plantilla guardada. Las listas ya creadas no cambian.' });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => {
                avisarError(e, e instanceof ErrorApi && e.estado === 412 ? 'Alguien ha cambiado la plantilla. Ciérrala y vuelve a abrirla.' : undefined);
            })
            .finally(() => { setOcupado(false); });
    };

    const archivar = () => {
        if (plantilla === null) {
            return;
        }
        setOcupado(true);
        ops.archiveGuestListTemplate(negocio, plantilla.id)
            .then(() => {
                avisar({ tipo: 'ok', texto: `«${plantilla.name ?? ''}» archivada: deja de copiarse a los eventos nuevos.` });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo={plantilla?.name ?? 'Nueva plantilla'}
            eyebrow="Plantilla de lista"
            subtitulo={plantilla === null ? 'Una lista tipo que copias a tus eventos.' : plantilla.apply_by_default ? 'Se copia a cada evento nuevo' : 'Solo si la eliges al crear las listas'}
            chip={plantilla === null ? undefined : plantilla.status === 'active' ? 'Activa' : 'Archivada'}
            chipEstado={plantilla?.status === 'active' ? 'confirmed' : 'neutral'}
            icono="layers"
            accion={puede ? (plantilla === null ? 'Crear plantilla' : 'Guardar') : ''}
            accionIcono={plantilla === null ? 'plus' : 'check'}
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo">
                {puede ? (
                    <>
                        <FormLista f={f} cambiar={(c) => { setF({ ...f, ...c }); }} errores={errores} plantilla whatsapp={ctx.whatsapp} movil={movil} onCanalesDeEnvio={() => { ctx.props.navegar(RUTA_CANALES_DE_ENVIO); }} />
                        <Bloque titulo="Al crear un evento">
                            <Interruptor etiqueta="Copiarla a cada evento nuevo" valor={f.aCadaEvento} onChange={(v) => { setF({ ...f, aCadaEvento: v }); }} ayuda="Si no, se elige al crear las listas." />
                        </Bloque>
                        {plantilla !== null && (
                            <Botones nota="Nunca se borra: las listas que ya se copiaron siguen igual.">
                                <Boton tipo="destructivo" icono="archive" onClick={archivar} disabled={ocupado}>Archivar</Boton>
                            </Botones>
                        )}
                    </>
                ) : (
                    plantilla !== null && (
                        <Bloque>
                            <span className="rv-ls-muestra" style={{ background: colorDe(plantilla.color) }} aria-hidden="true" />
                            <Dato icono="users" k="Aforo" v={plantilla.capacity == null ? 'Sin límite propio' : `${String(plantilla.capacity)} personas`} />
                            <Dato icono="clock" k="Hora límite" v={plantilla.door_cutoff_local_time == null ? 'Sin hora límite' : `Hasta las ${plantilla.door_cutoff_local_time}`} />
                            <Dato icono="user-plus" k="Acompañantes" v={acompanantes(plantilla.max_companions)} />
                            <Dato icono="instagram" k="Alta pública" v={plantilla.public_signup === true ? 'Instagram y web' : 'No'} />
                            {plantilla.status === 'archived' && <Caja icono="archive">Archivada: no se copia a los eventos nuevos.</Caja>}
                        </Bloque>
                    )
                )}
            </div>
        </PanelLateral>
    );
}
